import axios from "axios";
import React, { useState } from "react";

import { SpinnerComponent } from "../../commonComponent/SpinnerComponent";
import { APIKey, FormSectionElements, ProgramContainer } from "../../entities/formTemplateDataType";
import { LiveValidationErrMsg } from "../live-validation-errMsg/LiveValidationErrMsg";
import DataUtils from "../../utilities/DataUtils";
import { displayField, getRootComponent } from "../../utilities/ElementUtils";
import FormValidationUtils from "../../utilities/FormValidationUtils";
import SessionStorageService from "../../services/SessionStorageService";
import SectionUtils from "../../utilities/SectionUtils";
import { setBaseHeaders } from "../../utilities/submitUtils";
import Utils from "../../utilities/Utils";
import { SCRIPT_ENV } from "../../config/RuntimeConfig";

interface Props {
  field: FormSectionElements;
  labelControlArray: any;
  isEng: boolean;
  requiredField;
  callbackElement;
  callbackStepIndex;
}

interface State {
  isBusy: boolean;
}

/**
 * A functional component that handles the retrieval of data from API provided and populate to ProgramContainer.
 */
const SearchSingleItemFromApiComponent = ({field, labelControlArray, isEng, requiredField, callbackElement, callbackStepIndex}: Props) => {
  const [isBusy, setIsBusy] = useState(false);  // To show the spinner.

  const handleOnChange = (e) => {
    const elem = e.originalEvent ? e.originalEvent.target : e.target;
    const val = elem.value;

    field.Value = val;
    FormValidationUtils.markFieldDirty(field);
    FormValidationUtils.validateText(field);
    FormValidationUtils.runKeyStrokeJavascript(field);
    callbackElement(getRootComponent(field));
  }

  const handleOnBlur = (e) => {
    FormValidationUtils.evalRegularExpression(field)
    if (field.Valid) {
      if (field.Script) {
        const customJSFunc = eval(`(${field.Script})`);
        customJSFunc(field);
      }
      if (field.EventScript) {
        const customJSFunc = eval(`(${field.EventScript})`);
        customJSFunc(field, e);
      }
    }
    callbackElement(getRootComponent(field));
  }

  const handleButtonClick = async (captchaToken?: string) => {
    FormValidationUtils.evalRegularExpression(field);
    if (!field.Valid) {
      field.Valid = false;
      field.Touched = true;
      field.IsEmpty = false;
      callbackElement(field.parent.parent.parent);
      return;
    }

    invokeApi(field.Value, captchaToken);
  }
  
  const invokeApi = async (searchString: string, recaptchaToken: string): Promise<boolean> => {
    const programContainer = field.parent.parent.parent as ProgramContainer;
    const token = SessionStorageService.getAuthToken(programContainer.AuthRequired) ?? programContainer.Profile?.token;
    const api = field.Api;  // Assuming the event-scripts will populate the query-params.
    const apiKey: APIKey = (field.ApiKeyKey && field.ApiKeyValue) ? {key: field.ApiKeyKey, value: field.ApiKeyValue} : null;

    const headers = setBaseHeaders('application/json', token, apiKey);
    let response: any;

    try {
      setIsBusy(true);
      const requestData = constructRequestData(field);
      if (recaptchaToken) {
        requestData.token = recaptchaToken;
      }

      switch (field.SubmissionMethod) {
        case "post":
          response = await axios.post(api, requestData, { headers: headers });
          break;
        case "get":
          const configs = {
            ...{ headers }  // Headers
          };
          response = await axios.get(`${api}`, configs);
          break;
      }
      
      if (response.status >= 400) {
        if (response.status == 404) {
          handleSubmitError(response.data.statusCode);
        } else {
          handleSubmitException();
        }
        return false;
      }
    } catch (ex) {
      if (ex.response.status == 404) {
        handleSubmitError(ex.response.status);
      } else {
        console.log(ex);
        handleSubmitException();
      }
      return false;
    } finally {
      setIsBusy(false);
    }

    if (!response) {
      handleSubmitException();
      return false;
    }
  
    if (response.data) {  // Data is returned
      // To allow for post API-lookup process.
      if (Utils.isNotNullOrEmpty(field.PostScript)) {
        try {
        const functions = {
            resetFieldValues: ((field: FormSectionElements, exceptFields?: string[]) => SectionUtils.resetAllFieldValues(field, exceptFields)),
        }
          const customEventFunc = eval(`(${field.PostScript})`);
          if (typeof customEventFunc === 'function') {
            customEventFunc(field, SCRIPT_ENV, response, functions);
          }
        } catch (ex) {
          console.log(ex);
        }
      }
      // Populating the retrieved data to ProgramContainer.
      programContainer.Entity = response.data;
      DataUtils.assignFormValue(programContainer);
      callbackElement(programContainer);
      
      return true;
    } else {
        handleSubmitError(response.data.statusCode);
        return false;
    }
  }

  // TODO Implement for POST method call.
  const constructRequestData = (field: FormSectionElements): any => {
    return null;
  }

  const handleSubmitException = () => {
		if (field.SubmitButton?.apiErrorRedirectPage) {
			const sequence = Math.max(0, field.SubmitButton.apiErrorRedirectPage - 1);
      callbackStepIndex(sequence);
		}
  }

  const handleSubmitError = (statusCode) => {
    field.Valid = false;
    field.Touched = true;
    field.IsEmpty = false;
    callbackElement(field.parent.parent.parent);
  }

  const HtmlToReactParser = require('html-to-react').Parser;
  let hintHtml = isEng ? field.Hint : field.HintFR;

  const htmlToReactParser = new HtmlToReactParser();
  let hint;
  if (field.Hint||field.HintFR) {
    hint = htmlToReactParser.parse(hintHtml);
  }

  let displayfield: boolean = true;
  displayfield = displayField(field);

  field.Visible = displayfield;
  if (!displayfield) return null;

  const sectionReadOnly = field.parent?.parent?.SectionReadonly === true;
  field.ReadOnly = field.ReadOnly || sectionReadOnly;

  return (
    <>
      {field.Visible && (
        <div key={'div_' + field.Datatype +'_' + field.Id} id= {'div_' + field.Datatype +'_' + field.Id}>
          <div key={'fdLbl_div_' + field.Id} >
            {labelControlArray}
          </div>
          <div>
            <LiveValidationErrMsg
              field={field}
              isEng={isEng}
              key={"LiveValidationErrMsg_" + field.LoadBindName}/>
          </div>

          <div className="ontario-search__container">
            <div className="ontario-search__input-container">
              <input type="search" name="search" autoComplete="off" className="ontario-input ontario-search__input" 
                id={field.LoadBindName} 
                key={'fdIpt_' + field.LoadBindName}
                required={requiredField}
                value={field.Value}
                onBlur={handleOnBlur}
                onChange={handleOnChange}/>
              <input className="ontario-search__reset" id={field.LoadBindName + "-reset-button"} type="reset" aria-label="Clear field"
                style={{display: field.Value?.length == 0 ? "none" : "block"}}
                value=""
                onClick={() => {
                    field.Value = null;
                    field.SelectionCode = null;
                    field.Value = "";
                    callbackElement(getRootComponent(field));
                }} />
              <button className="ontario-search__submit" id={field.LoadBindName + "-submit-button"} type="submit" onClick={() => handleButtonClick()}>
                <span className="ontario-show-for-sr">Submit</span>
                <svg className="ontario-icon" focusable="false"  viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet">
                    <use href="#ontario-icon-search"></use>
                </svg>
              </button>
            </div>
            <div id="aria-live-region" className="ontario__visually-hidden" aria-live="polite"></div>
          </div>
          <div>
            <SpinnerComponent isBusy={isBusy}/>
          </div>
        </div>
      )}
    </>
  );
}

export default SearchSingleItemFromApiComponent;